import React, { useState } from 'react';
import { Button, Drawer, Tooltip } from 'antd';
import { Link } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import { CHEVRON_RIGHT, CHEVRON_UP } from '../icons';
import {
	sourceCountText,
	sourceKey,
	sourceListHref,
	sourceNotes,
	sourcesNeedCard,
	sourcesSummary,
} from '../../utils/combinedLists';
import { formatCount } from '../../utils/counts';

/**
 * THE SOURCE STRIP of a combined list (KPR-1022; docs/platform-standards.md
 * §5 "A list across collections"): which collections the list reads, each as
 * its collection badge with how many of its records match. Every count is a
 * door: it opens that source's own List, narrowed to its card, under the
 * filter it read (the query's `sources[].q`, in that collection's own ids).
 *
 * The badge is the collection's alone (Zach, 2026-10-20); the card is named
 * after it only when two sources share a collection or their cards are not
 * called the same. Nothing but the badge goes inside the pill: the count and
 * the notes sit after it.
 *
 * A source read only partly, or missing what the filter or sort names, says
 * so quietly after its count — never an error state: the rest of the list
 * still answers. A hand-picked card the person can no longer read is one quiet
 * line at the end.
 *
 * On a phone the strip folds into ONE button ("4 sources · 2 notes") that
 * opens a sheet with a 44px row per source.
 */
function SourceDoor({ source, withCard, row = false, onNavigate }) {
	const href = sourceListHref(source);
	const count = sourceCountText(source);
	const notes = sourceNotes(source);
	const cardName = withCard ? source?.card?.name : null;
	const label = [
		source?.collection?.name,
		cardName,
		count ? `${count} ${count === '1' ? 'match' : 'matches'}` : null,
		...notes,
	].filter(Boolean).join(', ');
	const body = (
		<>
			<span className="combined-sources__identity">
				<RecordBadge kind="collection" record={source.collection} title={source.collection?.name} size="xs" />
				{cardName ? <span className="combined-sources__card">{cardName}</span> : null}
			</span>
			{count ? <span className="combined-sources__count">{count}</span> : null}
			{row ? <span className="combined-sources__chevron" aria-hidden="true"><CHEVRON_RIGHT /></span> : null}
		</>
	);
	const door = href
		? <Link to={href} className="combined-sources__door" aria-label={`Open ${label}`} onClick={onNavigate}>{body}</Link>
		: <span className="combined-sources__door">{body}</span>;
	if (row) {
		return (
			<li className="combined-sources__row" data-testid="combined-source">
				{door}
				{notes.map(note => <span key={note} className="combined-sources__note">{note}</span>)}
			</li>
		);
	}
	return (
		<li className="combined-sources__item" data-testid="combined-source">
			{door}
			{notes.length ? (
				<Tooltip title={notes.join('. ')}>
					<span className="combined-sources__note">{notes.join(' · ')}</span>
				</Tooltip>
			) : null}
		</li>
	);
}

export default function CombinedListSources({ sources = [], isMobile = false, onEditSources = null }) {
	const [open, setOpen] = useState(false);
	const readable = sources.filter(source => source?.status !== 'unreadable' && source?.collection);
	const unreadable = sources.filter(source => source?.status === 'unreadable');
	const withCard = sourcesNeedCard(readable);
	const lostLine = unreadable.length
		? `${formatCount(unreadable.length, 'card you added is', 'cards you added are')} no longer readable`
		: null;

	if (isMobile) {
		return (
			<div className="combined-sources combined-sources--folded">
				<Button
					className="combined-sources__summary"
					block
					onClick={() => setOpen(true)}
					aria-haspopup="dialog"
					aria-expanded={open}
					data-testid="combined-sources-summary"
				>
					<span>{sourcesSummary(sources)}</span>
					<span aria-hidden="true"><CHEVRON_UP /></span>
				</Button>
				<Drawer
					placement="bottom"
					height="auto"
					open={open}
					onClose={() => setOpen(false)}
					title="Sources"
					className="combined-sources-sheet"
					destroyOnClose
				>
					<ul className="combined-sources__rows">
						{readable.map(source => (
							<SourceDoor key={sourceKey(source)} source={source} withCard={withCard} row onNavigate={() => setOpen(false)} />
						))}
					</ul>
					{lostLine ? <p className="combined-sources__note">{lostLine}</p> : null}
					{onEditSources ? (
						<Button type="link" className="combined-sources__edit" onClick={() => { setOpen(false); onEditSources(); }}>
							Edit sources
						</Button>
					) : null}
				</Drawer>
			</div>
		);
	}

	return (
		<div className="combined-sources" role="group" aria-label="Sources">
			<span className="combined-sources__total">{formatCount(readable.length, 'source', 'sources') || 'No sources'}</span>
			<ul className="combined-sources__list">
				{readable.map(source => (
					<SourceDoor key={sourceKey(source)} source={source} withCard={withCard} />
				))}
			</ul>
			{lostLine ? <span className="combined-sources__note">{lostLine}</span> : null}
		</div>
	);
}
